import React, { useState, useEffect, useMemo } from "react";
import { useAnnouncementCards, usePermission } from "hooks";
import { PermissionsRequired } from "library";
import { Page } from "components/Page";
import { useHub } from "hooks/graphql/useHubs";
import { AnnouncementCards } from "components/AnnouncementCards";
import { Banners } from "components/Banners";
import { BettingFilters } from "./BettingFilters";
import {
  useBettingFilters,
  type BettingRouteParams,
} from "./hooks/useBettingFilters";
import { BettingTabs } from "./BettingTabs";
import { BettingEventCards } from "./Events/BettingEventCards";
import { BettingPageSkeleton } from "./BettingPageSkeleton";
import { OutrightsList } from "./Events/OutrightsList";

import * as styles from "./Betting.module.scss";

const getTitles = (filters: BettingRouteParams, hubSlug: string) => {
  if (hubSlug === "esports") {
    if (filters?.hub) {
      return [filters.hub];
    }
  } else {
    if (filters?.sport) {
      return [filters.sport];
    }
  }
  return filters.sports ?? [];
};

type BettingProps = {
  pageContext: {
    section: string;
    canonical: string;
    hub: "racing" | "esports" | "sports";
  };
};

const Betting = ({ pageContext: { hub: hubSlug } }: BettingProps) => {
  const hub = useHub(hubSlug);

  const submitSGMBetPermission = usePermission("submitSGMBet");
  const [filters, setFilters] = useBettingFilters();
  const cards = useAnnouncementCards(hubSlug);

  const [page, setPage] = useState(filters.page ?? 1);
  const sportsTitles = useMemo(
    () => getTitles(filters, hubSlug),
    [filters, hubSlug],
  );

  useEffect(() => {
    if (sportsTitles.length > 1 && filters.sport) {
      setFilters({ sport: undefined });
    }
  }, [sportsTitles]);

  if (!hub) {
    return <BettingPageSkeleton />;
  }

  const canSGMBet = submitSGMBetPermission === "GRANTED";

  const hubName = hub.name || "Esports";

  const permissionsRequired = [
    "viewBettingMarkets",
    hub.slug === "esports" ? "viewEsportsMarkets" : "viewSportsMarkets",
  ];

  return (
    <Page
      variant="full"
      showSeparator={false}
      banner={<Banners />}
      title={hubName}
      hideTitleOnMobile
    >
      <PermissionsRequired
        permissionsRequired={permissionsRequired}
        skeleton={<BettingPageSkeleton />}
      >
        <div className={styles.betting}>
          <BettingTabs
            hub={hub}
            filters={filters}
            setFilters={setFilters}
            canSGMBet={canSGMBet}
            setPage={setPage}
          />

          <BettingFilters
            hubSlug={hubSlug}
            setPage={setPage}
            sportsTitles={sportsTitles}
            titles={hub.titles}
          />

          <div className={styles.content}>
            <AnnouncementCards
              cards={cards}
              className={styles.announcementCards}
            />

            {filters.tab === "next" ? (
              <BettingEventCards
                page={page}
                setPage={setPage}
                hub={hub}
                canSGMBet={canSGMBet}
                sportsTitles={sportsTitles}
              />
            ) : (
              <OutrightsList
                page={page}
                setPage={setPage}
                hub={hub}
                canSGMBet={canSGMBet}
                sportsTitles={sportsTitles}
              />
            )}
          </div>
        </div>
      </PermissionsRequired>
    </Page>
  );
};

export default Betting;
